import { useWorkosAuthEnabled } from "@/lib/workos-auth";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
  Suspense,
} from "react";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useApi } from "@/hooks/useApi";
import { useSectionTiming } from "@/hooks/useSectionTiming";
import { QueryErrorResetBoundary, useQuery, useQueryClient } from "react-query";
import {
  fetchOrgs,
  GithubSsoReauthorizationRequiredException,
  type GithubInstallations,
} from "@/hooks/useConnectedGithubOrgs";
import axios, { type AxiosError, type AxiosInstance } from "axios";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { usePostHog } from "posthog-js/react";
import SignedOut from "./SignedOut";
import { SidebarLayoutSkeleton } from "./SidebarLayout";
import { BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/blacksmith-ui/alert-dialog";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { useTheme } from "next-themes";
import * as z from "zod";
import {
  emitGithubFirstSignupGtmEvent,
  readSignupConversionId,
  shouldEmitGithubFirstSignupGtm,
  stripGithubFirstSignupQuery,
} from "@/lib/githubAuthGtm";
import {
  clearOauthSuccessAppLoadGtmSessionFlag,
  emitOauthSuccessAppLoadGtmOnce,
} from "@/lib/oauthSuccessAppLoadGtm";
import { clearRdtCid } from "@/lib/appFirstTouchAttribution";
import { clearCodesmithLayoutCache } from "./sidebar/useCodesmithEnabledCache";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
import { authHadSession } from "@/lib/authHadSession";
import { identifyUserForOrg } from "@/lib/posthog-identify";

const userSchema = z.object({
  id: z.number(),
  is_superuser: z.boolean(),
  name: z.string(),
  username: z.string(),
  email: z.string(),
  avatar_url: z.string(),
  active_org_name: z.string().nullable().optional(),
  heard_from: z.string().nullable().optional(),
  first_name: z.string().nullable().optional(),
  session_count: z.number().int().nonnegative().nullable().optional(),
  // Whether this user should see in-progress/queued statuses in runs analytics.
  // Computed server-side as (rollout enabled || is_superuser). Optional to
  // tolerate older cached responses during deploys.
  show_live_statuses: z.boolean().optional(),
  control_plane_static_ips: z.array(z.string()).optional(),
  codesmith_ack_preview_enabled: z.boolean().nullable().optional(),
  codesmith_canvas_enabled: z.boolean().nullable().optional(),
  codesmith_canvas_animation_enabled: z.boolean().nullable().optional(),
  codesmith_canvas_refresh_enabled: z.boolean().optional(),
});

export type User = z.infer<typeof userSchema>;

interface UserContext {
  user: User | undefined;
  logout: () => void;
  setLoginModalOpen: (open: boolean) => void;
}

export const AuthContext = createContext<UserContext>({
  user: undefined,
  logout: () => {},
  // NB: Some other components can use this to effectively force the
  // user to refresh their session.
  setLoginModalOpen: () => {},
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

async function fetchUser(api: AxiosInstance) {
  return api.get(`${process.env.NEXT_PUBLIC_API_URL}/api/user`);
}

// useLoginUser is a special hook meant to be used in the AuthProvider component.
// It intentionally does not use suspense, and instead returns a loading state and
// error. The AuthProvider component uses this hook to determine whether the user
// is logged in or not. If the user is not logged in, we show the SignedOut component.
export function useLoginUser() {
  const api = useApi();
  const { data, isLoading, error, isError, status } = useQuery<
    User,
    AxiosError
  >(
    "user",
    async () => {
      const response = await fetchUser(api);
      return response.data;
    },
    {
      suspense: false,
      // NB: We don't want to retry the query if it fails. If the user is not logged in, we will
      // show them the login modal. Re-running the query will cause the page/modal to flicker.
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      useErrorBoundary: false,
    },
  );

  return {
    user: data,
    isLoading,
    error,
    isError,
    status,
  };
}

export interface SessionExpirationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function SessionExpirationModal({
  open,
  setOpen,
}: SessionExpirationModalProps) {
  const { workosAuthEnabled, isLoading: isFlagLoading } =
    useWorkosAuthEnabled();
  const { resolvedTheme } = useTheme();
  const router = useRouter();

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Your session has expired. Please log in again.
          </AlertDialogTitle>
          <AlertDialogDescription>
            You will be redirected to the login page.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            disabled={isFlagLoading}
            className="flex gap-2"
            variant="default"
            onClick={() => {
              const redirectUrl = encodeURIComponent(window.location.href);
              const loginUrl = `${process.env.NEXT_PUBLIC_API_URL}/login/${
                workosAuthEnabled ? "workos" : "github"
              }?redirect=${redirectUrl}`;
              if (isCodesmithExtensionEmbed(router.query.embed)) {
                window.open(loginUrl, "_blank");
                return;
              }
              window.location.href = loginUrl;
            }}
          >
            {resolvedTheme === "dark" ? (
              <BlackGitHubIcon />
            ) : (
              <WhiteGitHubIcon />
            )}
            Login
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const AUTH_BYPASS_PATHS = ["/oauth/authorize", "/cli/auth"];

// Pages that skip the login card for logged-out visitors and go straight to
// GitHub OAuth; the login redirect returns them to the same URL.
const AUTO_LOGIN_PATHS = ["/autofix-settings", "/[org]/autofix-settings"];

const getServerAuthHadSession = () => false as const;
const subscribeToNothing = () => () => {};

function isUnauthorizedError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

function LogomarkLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-32 h-32">
        <BlacksmithLogomarkAnimationSVG />
      </div>
    </div>
  );
}

/**
 * A query 401 captured while auth is pending stays behind the skeleton until
 * resetKeys reset the boundary. Other errors propagate to the app boundary.
 */
function PendingAuthFallback({
  error,
  authPending,
}: FallbackProps & { authPending: boolean }) {
  // react-error-boundary v4 renders fallback once before applying changed resetKeys.
  const [caughtWhileAuthPending] = useState(authPending);

  if (caughtWhileAuthPending && isUnauthorizedError(error)) {
    return <SidebarLayoutSkeleton />;
  }
  throw error;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const firstSignupGtmHandledRef = useRef(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const logout = useCallback(() => {
    authHadSession.clear();
    clearCodesmithLayoutCache();
    window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/logout`;
  }, []);
  const { user, isLoading, isError, error, status } = useLoginUser();

  useSectionTiming("auth", status);

  // Start the cookie-authenticated org request with the user request so route
  // queries that suspend on org data share the same initial network window.
  useEffect(() => {
    queryClient.prefetchQuery(
      "connectedGithubOrgs",
      () => fetchOrgs(api),
      { staleTime: Number.POSITIVE_INFINITY }, // Match useConnectedGithubOrgs config
    );
  }, [api, queryClient]);

  useEffect(() => {
    if (!router.isReady || firstSignupGtmHandledRef.current) {
      return;
    }
    if (!shouldEmitGithubFirstSignupGtm(router.query)) {
      return;
    }
    firstSignupGtmHandledRef.current = true;
    const conversionId = readSignupConversionId(router.query);
    emitGithubFirstSignupGtmEvent(conversionId);
    // Clear the stored rdt_cid now that it has been consumed by the signup.
    // This prevents it from being reused for a future signup on the same
    // browser (e.g. a shared computer or a second account creation).
    clearRdtCid();
    const nextQuery = stripGithubFirstSignupQuery(router.query);
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true },
    );
  }, [router]);

  const isUnauthorized = useMemo(() => {
    return isError && error?.response && error?.response.status === 401;
  }, [isError, error]);

  const shouldBypassAuth = useMemo(() => {
    return AUTH_BYPASS_PATHS.some((path) => router.pathname.startsWith(path));
  }, [router.pathname]);

  useEffect(() => {
    if (!router.isReady || !user || shouldBypassAuth) {
      return;
    }
    emitOauthSuccessAppLoadGtmOnce();
  }, [router.isReady, user, shouldBypassAuth]);

  const hadSession = useSyncExternalStore(
    subscribeToNothing,
    authHadSession.read,
    getServerAuthHadSession,
  );

  // react-query keeps the last user across a later 401, so a defined user means
  // the first auth attempt succeeded and a 401 now is an expired session.
  const firstAttemptFinished = user !== undefined;
  const sessionExpired =
    !isLoading && isUnauthorized === true && firstAttemptFinished;

  useEffect(() => {
    if (sessionExpired) {
      clearOauthSuccessAppLoadGtmSessionFlag();
    }
  }, [sessionExpired]);

  useEffect(() => {
    if (user) authHadSession.write(user);
  }, [user]);

  // Clear the session hint on the sign-in page so the next load shows the minimal loader
  useEffect(() => {
    if (isUnauthorized && !firstAttemptFinished && !shouldBypassAuth) {
      authHadSession.clear();
      clearOauthSuccessAppLoadGtmSessionFlag();
    }
  }, [isUnauthorized, firstAttemptFinished, shouldBypassAuth]);

  // Observe connected org state so pages using only non-suspense org hooks can
  // still show the dedicated SSO re-authorization screen.
  const { data: connectedGithubOrgs, error: orgsError } =
    useQuery<GithubInstallations>("connectedGithubOrgs", () => fetchOrgs(api), {
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY,
    });

  useEffect(() => {
    if (user) {
      identifyUserForOrg(posthog, user, connectedGithubOrgs?.active_org_name);
    }
  }, [connectedGithubOrgs?.active_org_name, posthog, user]);

  if (isError && !isUnauthorized) {
    throw error;
  }

  if (
    !shouldBypassAuth &&
    orgsError instanceof GithubSsoReauthorizationRequiredException
  ) {
    throw orgsError;
  }

  const authPending = isLoading && !firstAttemptFinished;

  if (authPending && hadSession === "superuser") {
    return <SidebarLayoutSkeleton />;
  }

  if (authPending && hadSession !== "1") {
    return <LogomarkLoader />;
  }

  // If the user is not logged in but they had the app open, this implies that
  // their session has expired. In that case, we will show them a login modal.
  // Otherwise, if the user is not logged in and they never had a successful login
  // attempt in this session, we show them the `SignedOut` component.
  // Exception: Pages that handle their own auth flow (like OAuth authorize) should
  // be allowed to render and handle auth themselves.
  if (isUnauthorized && !firstAttemptFinished && !shouldBypassAuth) {
    return (
      <>
        <SignedOut autoLogin={AUTO_LOGIN_PATHS.includes(router.pathname)} />
      </>
    );
  }
  return (
    <AuthContext.Provider value={{ user, logout, setLoginModalOpen }}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={(props) => (
              <PendingAuthFallback {...props} authPending={authPending} />
            )}
            resetKeys={[authPending]}
          >
            {/* Children can suspend before this provider first commits; at the
                layout boundary that discards the layout's query client and
                re-suspends on a fresh cache in a loop. */}
            <Suspense fallback={<LogomarkLoader />}>{children}</Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
      <SessionExpirationModal
        open={loginModalOpen || sessionExpired}
        setOpen={setLoginModalOpen}
      />
    </AuthContext.Provider>
  );
}
